import { useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  setServiceUsersData,
  updateServiceUserData,
} from "../../State/slices/serviceUsersData-slice";
import { setServiceUsers } from "../../State/slices/serviceUsers-slice";
import { setUser } from "../../State/slices/user-slice";
import {
  setFetchInitialState,
  setLoadingBaseData,
  setRefreshStaff,
  // setCommunityAuth,
} from "../../State/slices/session-slice";
import { AppDispatch } from "../../State/store";
import {
  getCheckAuthState,
  getHubRiskScoreV2,
  postHubsRiskScores,
  getOrganisationHubs,
  HubRiskScoreV2BodyType,
} from "@intelligentlilli/api-layer";
// Utils
import {
  calcBehaviourRisk,
  formatUserHubs,
} from "@intelligentlilli/lilli-utils";
import { isSameDay } from "date-fns";
import { getUserIsManagerOrAdmin } from "../Utils";

// Exit the loading animation when:
// 1. For installer-only roles: when getOrgHubs has been fetched
// 2. Fetching risk scores has completed for all residents
const completeLoadingAnimation = (dispatch: AppDispatch) => {
  dispatch(setLoadingBaseData(4));
  setTimeout(() => {
    dispatch(setLoadingBaseData(0));
  }, 250);
};

type FetchOrganisationHubs = {
  server: string;
  organisationId: number;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
};

const fetchOrganisationHubs = ({
  server,
  organisationId,
  dispatch,
  navigate,
}: FetchOrganisationHubs) => {
  // If the user is a manager get all organisation residents and persist to redux
  getOrganisationHubs(server, organisationId, "web")
    .then((res) => {
      if (res.ok) {
        // Persisting a hubs object to state ready to receive hub data.
        dispatch(setServiceUsersData(formatUserHubs(res.data)));
        dispatch(setServiceUsers(res.data));
        dispatch(setLoadingBaseData(3));
        return res.data;
      }
    })
    .then((result) => fetchRiskData(result, server, dispatch, navigate))
    .catch((err) => {
      completeLoadingAnimation(dispatch);
      if (err.status === 401) {
        console.log("getOrganisationHubs error:", err);
      }
      console.log(err);
    });
};

const saveSURiskData = (
  dispatch: AppDispatch,
  riskScore: HubRiskScoreV2BodyType
) => {
  const allBehavioursExceptIndependence = riskScore.dayRatings.map((day) => {
    // Remove the independence behaviour from the day ratings
    const { independence, ...rest } = day.details;
    return {
      ...day,
      details: { ...rest },
    };
  });
  const amendedRiskScore = {
    ...riskScore,
    dayRatings: allBehavioursExceptIndependence,
  };

  // Function for getting the number of occurances of each risk level within the daily risk values
  const countOccurrences = (
    arr: { dayRating: number | null }[],
    val: number
  ): number => arr.reduce((a, v) => (v.dayRating === val ? a + 1 : a), 0);

  // Occurances of eack risk level in the date range
  const countAtRisk = countOccurrences(amendedRiskScore.dayRatings, 2);
  const countUnexpected = countOccurrences(amendedRiskScore.dayRatings, 1);
  const countExpected = countOccurrences(amendedRiskScore.dayRatings, 0);
  // Saving this week's number of days at each risk level
  dispatch(
    updateServiceUserData({
      hubId: riskScore.hubId,
      update: {
        riskScores: riskScore.dayRatings,
        weekRisks: {
          atRisk: countAtRisk,
          unexpected: countUnexpected,
          expected: countExpected,
        },
        riskStatus: amendedRiskScore.dayRatings.filter((dayRating) =>
          isSameDay(new Date(), new Date(dayRating.date))
        )?.[0].dayRating,
        riskStatusSustenance: calcBehaviourRisk(
          riskScore.dayRatings,
          "sustenance"
        ),
        riskStatusMovement: calcBehaviourRisk(riskScore.dayRatings, "movement"),
        riskStatusIndependence: calcBehaviourRisk(
          riskScore.dayRatings,
          "independence"
        ),
        riskStatusSleep: calcBehaviourRisk(riskScore.dayRatings, "sleep"),
      },
    })
  );
};

// There are two possible ways to get risk data. The slow way; which is straight to the API and making a call for each SU.
// The fast way is a single call to our CloudFlare workers to serve up cached data. This uses a new endpoint that doesn't exist in the actual API.
async function fetchRiskData(
  serviceUsers: any,
  server: string,
  dispatch: AppDispatch,
  navigate: NavigateFunction
) {
  // First filter out residents pre installation as they won't have any risk data so will be unnecessary calls
  const sUsPostInstallation =
    serviceUsers?.filter(
      (serviceUser) =>
        serviceUser?.serviceUserProperties?.installationStatus === "complete"
    ) || [];

  // Create an array of the residents' id's
  const serviceUserIds =
    sUsPostInstallation?.map((serviceUser) => serviceUser.id) || [];

  const toFetch = [];
  // Split the behaviours into groups of three per page
  while (serviceUserIds?.length > 0) {
    toFetch.push(serviceUserIds.splice(0, 48));
  }

  // If the organisation does not yet have any installed hubs simply complete the loading spinner.
  if (toFetch.length === 0) {
    completeLoadingAnimation(dispatch);
    dispatch(setRefreshStaff(true));
  }

  // Where there are more than 48 residents to fetch we want to keep track of whether we have fetched all their risk data
  const numOfGroupsToFetch = toFetch.length;
  let fetchedGroups = 0;

  // When a group finished fetching their risk data this function is called.
  const incrementGroupsFetched = () => {
    fetchedGroups++;
    // Finally finish off the loading animation by setting loadingBaseData when all the data has been received
    if (fetchedGroups === numOfGroupsToFetch) {
      completeLoadingAnimation(dispatch);
      dispatch(setRefreshStaff(true));
    }
  };

  toFetch.map(async (serviceUsersSubset) => {
    try {
      // Call the CloudFlare endpoint that gets all the resident risk scores in one call
      const hubsRiskScores = await postHubsRiskScores(
        server,
        serviceUsersSubset,
        "web"
      );
      // If that's successful, save all the risk data in redux & local storage
      if (hubsRiskScores.ok) {
        hubsRiskScores?.data?.forEach((riskScore) => {
          if (riskScore?.dayRatings) {
            saveSURiskData(dispatch, riskScore);
          }
        });
        incrementGroupsFetched();
      }
      // If the request fails for whatever reason, we can fall back to the slow way
      if (!hubsRiskScores.ok || hubsRiskScores.message) {
        const promises = serviceUsersSubset?.map((lilliUser) =>
          getHubRiskScoreV2(
            server,
            lilliUser,
            7,
            new Date().toISOString(),
            "web"
          ).then((res) => {
            if (res?.ok) {
              saveSURiskData(dispatch, res?.data);
            }
          })
        );
        await Promise.all(promises);
        incrementGroupsFetched();
      }
    } catch (error) {
      if (error.status === 401) {
        completeLoadingAnimation(dispatch);
        return;
      }
      // Where the user is working in dev (i.e. cloudflare isn't intercepting requests) this endpoint will not exist so we need to fall back to the slow way
      const promises = serviceUsersSubset?.map((lilliUser) =>
        getHubRiskScoreV2(
          server,
          lilliUser,
          7,
          new Date().toISOString(),
          "web"
        ).then((res) => {
          if (res?.ok) {
            saveSURiskData(dispatch, res?.data);
          }
        })
      );
      await Promise.all(promises);
      incrementGroupsFetched();
    }
  });
}

export const useFetchInitialData = (
  server: string,
  setCheckedAuth: (arg: boolean) => void,
  dispatch: AppDispatch,
  fetchInitialState: boolean
) => {
  const navigate = useNavigate();
  // Check the auth state of the user and persisting the user to state
  useEffect(() => {
    if (fetchInitialState) {
      dispatch(setFetchInitialState(false));
      dispatch(setLoadingBaseData(1));
      getCheckAuthState(server, "web")
        .then((res) => {
          if (res.ok) {
            // Persisting the user to state
            dispatch(setUser(res?.data));

            // if res is okay, fetch the zipline-auth headers for the community site
            // if (res.headers["zipline-auth"]) {
            //   dispatch(setCommunityAuth(res.headers["zipline-auth"]));
            // }

            // Return response
            return res?.data;
          }
        })
        .then((result) => {
          // If the user is not authorised skip the next step
          if (!result) {
            dispatch(setLoadingBaseData(0));
            return null;
          }
          dispatch(setLoadingBaseData(2));
          /*
            If the user is manager/admin OR is "just an installer", 
            Then set serviceUsers (Redux) to all hubs in the organisation
            For all other roles, i.e. "friend-or-family" and "user" (aka staff) we assign their associated hubs to serviceUsers
          */
          const userIsManagerOrAdmin = getUserIsManagerOrAdmin(result.roles);

          if (userIsManagerOrAdmin) {
            fetchOrganisationHubs({
              server,
              organisationId: result.organisationId,
              dispatch,
              navigate,
            });
          } else {
            dispatch(setLoadingBaseData(3));
            dispatch(setServiceUsers(result.hubs));
            fetchRiskData(result.hubs, server, dispatch, navigate);
            // Seperately create an object of SUs where SU id is the key. This is for persisting the risk and behaviours data later on
            dispatch(setServiceUsersData(formatUserHubs(result.hubs)));
          }
        })
        .catch((err) => {
          console.log("getCheckAuthState - fetchInitialState error:", err);
          dispatch(setLoadingBaseData(0));
          navigate("/outage-detected");
          return null;
        })
        .finally(() => {
          setCheckedAuth(true);
        });
    }
  }, [
    navigate,
    fetchInitialState,
    dispatch,
    server,
    setCheckedAuth,
  ]);
};
